import React, {useMemo} from 'react';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import WarningMessage from '../WarningMessage';
import ContactUsAction from 'app/Components/Shared/ContactUs/ContactUsAction';
import {contactUsEnergy, contactUsEnergyMob} from '../../Constants/debtPaymentEnum';
import {BanTypeEnum} from '../../Constants/banTypeEnum';
import {isMobile} from 'react-device-detect';
import {scheduledDate, getNonDebtFinancialDisplayContext} from '../../utils/banTypeClassifier';
import BitPendingLabel from '../BitPendingLabel';
import {buildUrlAndOpenWindow} from 'app/utilities/StringHelpers/StringHelpers';
import {useBanUpdatePaymentClick} from '../../hooks/useBanUpdatePaymentClick';
import Image from 'app/Components/Shared/Image/image';

const scheduledDebtAmountDisplay = (raw) => {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }
  const num = typeof raw === 'number' ? raw : parseFloat(String(raw).trim().replace(/,/g, ''));
  if (Number.isNaN(num)) {
    return String(raw);
  }
  const magnitude = Math.abs(num);
  if (num < 0) {
    return `₪-${magnitude}`;
  }
  return `₪${magnitude}`;
};

const PaymentIssueFinancialDetails = ({displayBan, informationType, yDaysCount, onPaymentClick}) => {
  switch (informationType) {
    case BanTypeEnum.BAN_WITH_FUTURE_INVOICE:
    case BanTypeEnum.BAN_WITH_BALANCE_FOR_CHARGING: {
      const getDebtAmount = () =>
        informationType === BanTypeEnum.BAN_WITH_FUTURE_INVOICE ? displayBan.totalDueAmount : displayBan.arBalance;
      const showPrepay =
        (informationType === BanTypeEnum.BAN_WITH_FUTURE_INVOICE && parseFloat(displayBan.totalDueAmount) > 0) ||
        (informationType === BanTypeEnum.BAN_WITH_BALANCE_FOR_CHARGING && parseFloat(displayBan.arBalance) > 0);

      return (
        <div className='payment-issue-financial-details payment-issue-financial-details--scheduled'>
          {displayBan.payPendingStatus && <BitPendingLabel />}
          <div
            dangerouslySetInnerHTML={{__html: scheduledDate(displayBan)}}
            className='scheduled-payment-ban-status__debt-label payment-issue-financial-details__schedule-text'
          />
          <div dir='ltr' className='scheduled-payment-ban-status__debt-amount__future'>
            {scheduledDebtAmountDisplay(getDebtAmount())}
          </div>
          <div className='payment-issue-ban-status__action-links'>
            {displayBan.invoiceUrl && (
              <button type='button' className='link' onClick={() => buildUrlAndOpenWindow(displayBan.invoiceUrl)}>
                לצפייה בחשבונית
              </button>
            )}
            {showPrepay && (
              <button type='button' className='link' onClick={onPaymentClick}>
                לתשלום מראש
              </button>
            )}
          </div>
        </div>
      );
    }
    case BanTypeEnum.BAN_NO_DEBT_NO_PREVIOUS_DEBT:
      return (
        <div className='payment-issue-financial-details payment-issue-financial-details--paid'>
          <div className='active-ban-status__paid-label'>
            <span>
              <Image src={require('../../images/check.svg')} alt='checked' />
            </span>
            <span>החשבון שולם</span>
          </div>
          <div
            dangerouslySetInnerHTML={{__html: scheduledDate(displayBan, yDaysCount)}}
            className='scheduled-payment-ban-status__debt-label'
          />
          {displayBan.invoiceUrl && (
            <div className='invoice-link'>
              <div type='button' className='link' onClick={() => buildUrlAndOpenWindow(displayBan.invoiceUrl)}>
                לצפייה בחשבוניות אחרונות
              </div>
            </div>
          )}
        </div>
      );
    case BanTypeEnum.BAN_NO_DEBT_BUT_HAD_DEBT_IN_INVOICE: {
      const {invoice, sent} = scheduledDate(displayBan);
      const getMessage = () => {
        return `החיוב בטיפול ולכן לא ניתן לשלם כעת במידה
    <br />
    והחיוב לא יעבור, נשלח עדכון ב-SMS`;
      };
      return (
        <div className='payment-issue-financial-details payment-issue-financial-details--in-process'>
          <div className='payment-in-process-ban-status__balance'>
            <div className='payment-in-process-ban-status__balance-label'>{sent}</div>
            <div className=' payment-in-process-ban-status__balance-amount'>
              <div>
                <img src={require('../../images/dot.png')} alt='dot' />
              </div>
              <div className='balance-label'>
                <div>חוב בסך</div>
                <div className='amount'>₪{displayBan.pastDueAmt}</div>
              </div>
            </div>
            <div className=' payment-in-process-ban-status__balance-amount'>
              <div>
                <img src={require('../../images/dot.png')} alt='dot' />
              </div>
              <div className='balance-label'>
                <div>{invoice} </div>
                <div className='payment-in-process-ban-status__balance-amount-value'>
                  <span className='amount ml-2'>₪{displayBan.totalDueAmount}</span>
                  {displayBan.invoiceUrl && (
                    <span className='invoice-link'>
                      <div type='button' className='link' onClick={() => buildUrlAndOpenWindow(displayBan.invoiceUrl)}>
                        לצפייה בחשבונית
                      </div>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <WarningMessage message={getMessage()} />
        </div>
      );
    }
    default:
      return null;
  }
};

const PaymentIssueBanStatus = ({banData, onPaymentClick, yDaysCount, paymentConfiguration}) => {
  const onUpdatePaymentClick = useBanUpdatePaymentClick(banData);

  const {informationType, nextPaymentDueDate} = useMemo(() => {
    if (!paymentConfiguration) {
      return {informationType: null, nextPaymentDueDate: null};
    }
    return getNonDebtFinancialDisplayContext(banData, paymentConfiguration);
  }, [
    banData,
    paymentConfiguration?.paymentStatusDisplayDaysX,
    paymentConfiguration?.paymentStatusDisplayDaysY,
    paymentConfiguration?.energyPaymentStatusDisplayDaysX,
    paymentConfiguration?.energyPaymentStatusDisplayDaysY,
  ]);

  const displayBan = useMemo(() => {
    if (!informationType) return null;
    return {
      ...banData,
      banType: informationType,
      ...(nextPaymentDueDate ? {nextPaymentDueDate} : {}),
    };
  }, [banData, informationType, nextPaymentDueDate]);

  return (
    <div className={`debt-ban-status payment-issue-ban-status`}>
      <div className='payment-issue-ban-status__update-row'>
        <div className='payment-issue-ban-status__warning-inline'>
          <span className='payment-issue-ban-status__warning-badge'>
            <img src={require('../../images/alert-yellow.png')} alt='' />
          </span>
          <span className='payment-issue-ban-status__warning-text'>אמצעי התשלום לא מעודכן</span>
        </div>
        {banData.isEnergy ? (
          <div className='debt-ban-status__btn energy'>
            <ContactUsAction contentData={isMobile ? contactUsEnergyMob : contactUsEnergy} />
          </div>
        ) : (
          <div className='debt-ban-status__btn'>
            <BtnSubmit text='לעדכון' onclick={onUpdatePaymentClick} type='button' />
          </div>
        )}
      </div>

      {displayBan && informationType ? (
        <>
          <div className='ban-status-card__section-divider' />
          <div className='payment-issue-ban-status__financial'>
            <PaymentIssueFinancialDetails
              displayBan={displayBan}
              informationType={informationType}
              yDaysCount={yDaysCount}
              onPaymentClick={onPaymentClick}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PaymentIssueBanStatus;
